import type { Club } from "../../lib/api/lidoo.functions";

// Most clubs have no photo yet, and will not until their owner uploads one
// through the dashboard. We will not put a stock or generated image on a real,
// named business — a picture of a beach that isn't theirs is a lie about what
// you are booking. So the no-photo state has to be designed rather than empty:
// a drawn plate carrying the coast, the parasol mark and the club's initials,
// which sits next to a real photograph without looking broken.
//
// The palette is derived from the slug, so a club always gets the same plate
// and neighbouring cards in a grid don't come out identical.
//
// The viewBox is deliberately close to the card's own aspect (roughly 2.2:1).
// An earlier 400x240 box was letterboxed by `slice` and sheared the tops off
// the initials.
const PALETTES: { sky: string; sea: string; sand: string; ink: string }[] = [
  { sky: "#DCE6F5", sea: "#2C4FD6", sand: "#EFE4CE", ink: "#1C2430" },
  { sky: "#E3EDE8", sea: "#1F7A6B", sand: "#EFE7D6", ink: "#1C2430" },
  { sky: "#F1E4DC", sea: "#C4643C", sand: "#F0E5D2", ink: "#1C2430" },
  { sky: "#E7E3F0", sea: "#5B4BA8", sand: "#EFE8D9", ink: "#1C2430" },
  { sky: "#DFEAF0", sea: "#2E7FA6", sand: "#F0E6D4", ink: "#1C2430" },
];

function paletteFor(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return PALETTES[h % PALETTES.length];
}

// Generic words are dropped so "Lido Fontane Bianche" reads FB rather than LF,
// but a name that is *only* generic words must still produce something — and a
// single remaining word gives two letters rather than a lonely initial.
const GENERIC = /^(the|le|la|il|el|los|las|lido|beach|club|bar|plage|playa|spiaggia|stabilimento|balneare|di|de|del|du|da|des|of|and|y|e)$/i;

function initials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N} ]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
  const meaningful = words.filter((w) => !GENERIC.test(w));
  const pick = meaningful.length ? meaningful : words;
  if (pick.length === 1) return pick[0].slice(0, 2).toUpperCase();
  return pick
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

// The card slot is a wide letterbox; the club-page hero is much closer to
// square. One viewBox cannot serve both under `slice` without shearing the
// initials off one axis or the other, so the plate carries two geometries.
const GEOMETRY = {
  // card matches the h-52 slot in a 3-up grid (~1.87:1); hero matches the
  // taller slot on the club page. Keep these in step with the CSS heights —
  // a mismatch is what shears the initials off the sides.
  card: { w: 400, h: 234, seaY: 122, seaH: 42, markX: 296, markY: 96, markS: 0.44, initY: 96, initSize: 66, regionY: 208 },
  hero: { w: 400, h: 300, seaY: 158, seaH: 52, markX: 292, markY: 128, markS: 0.5, initY: 118, initSize: 84, regionY: 268 },
} as const;

export function ClubPlate({
  club,
  variant = "card",
  className = "",
}: {
  // Only what the plate draws, so the slimmer homepage rows can use it too.
  club: Pick<Club, "slug" | "name" | "town" | "region_label">;
  variant?: keyof typeof GEOMETRY;
  className?: string;
}) {
  const p = paletteFor(club.slug);
  const label = initials(club.name);
  const g = GEOMETRY[variant];

  return (
    <svg
      viewBox={`0 0 ${g.w} ${g.h}`}
      className={`h-full w-full ${className}`}
      role="img"
      aria-label={`${club.name}, ${club.town}`}
      preserveAspectRatio="xMidYMid slice"
    >
      <rect width={g.w} height={g.h} fill={p.sky} />

      {/* Sea: a flat band with a couple of swells so it reads as water. */}
      <rect y={g.seaY} width={g.w} height={g.seaH} fill={p.sea} />
      <path
        d={`M0 ${g.seaY + 4}c34 0 34-7 68-7s34 7 68 7 34-7 68-7 34 7 68 7 34-7 68-7 34 7 60 7v-12H0z`}
        fill={p.sea}
      />
      <rect y={g.seaY + g.seaH} width={g.w} height={g.h - g.seaY - g.seaH} fill={p.sand} />

      {/* The brand mark, small and low-key, standing on the sand. */}
      <g transform={`translate(${g.markX} ${g.markY}) scale(${g.markS})`} opacity="0.92">
        <path d="M60 0C33 0 11 17 11 38h98C109 17 87 0 60 0z" fill={p.ink} />
        <rect x="55" y="38" width="10" height="54" rx="5" fill={p.ink} />
        <rect x="8" y="88" width="112" height="15" rx="7" fill={p.ink} />
        <path d="M10 94 L-12 70a8 8 0 0 1 11-10l22 24z" fill={p.ink} />
        <rect x="26" y="103" width="9" height="24" rx="4" fill={p.ink} />
        <rect x="94" y="103" width="9" height="24" rx="4" fill={p.ink} />
      </g>

      <text
        x="26"
        y={g.initY}
        fill={p.ink}
        fontSize={g.initSize}
        fontWeight="900"
        letterSpacing="-3"
        fontFamily="Satoshi, system-ui, sans-serif"
      >
        {label}
      </text>
      <text
        x="27"
        y={g.regionY}
        fill={p.ink}
        fontSize="13"
        fontWeight="600"
        letterSpacing="1.6"
        fontFamily="JetBrains Mono, ui-monospace, monospace"
      >
        {club.region_label.toUpperCase()}
      </text>
    </svg>
  );
}
